import PersonIcon from '@mui/icons-material/Person';
import { Box, Grid, Typography } from '@mui/material';
import {
  GridColDef,
  GridColumnGroupingModel,
  GridEventListener,
  GridToolbarExport,
  useGridApiRef
} from '@mui/x-data-grid-pro';

import { default as React, useEffect, useState } from 'react';
import { useSubjectTrackingDataMutation } from 'src/services/SubjectTracking';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { clearSubjectInfo, setSelectedSubject } from 'src/store/SelectedSubject';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import SubjectTrackingDetails from './PrescreenDetails/SubjectTrackingDetails';
import './SubjectTracking.scss';
import prescreenMockData from './mockData.json';
import { setPreScreenGridRowsData } from 'src/store/preScreenGridRowsData';
import { DataTable } from '@pihealth/common-ui-library';
import moment from 'moment';

const SubjectTrackingPage: React.FunctionComponent<any> = () => {
  const dispatch = useAppDispatch();
  const [showSubjectTraking, setShowSubjectTraking] = useState(true);
  const [showSubjectDetails, setShowSubjectDetails] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const mockData: any = prescreenMockData;
  const apiRef = useGridApiRef();
  const site = useAppSelector(state => state.site.selectedSite);
  const selectedStudy = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const [rows, setRows] = useState<any>([]);
  const [preScreenGridColumns, setPreScreenGridColumns] = useState<GridColDef[]>([]);
  const [preScreencolumnGroupingModel, setPreScreencolumnGroupingModel] = useState<GridColumnGroupingModel>([]);
  const [filterObject, setFilterObject] = useState<any>();
  const { preferedDateFormat, userPreferedDateFormat } = usePreferedDateFormat();
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const [prescreenId, setPrescreenId] = useState<string>(
    useAppSelector(state => state.selectedSubjectInfo.selected_prescreen_id)
  );
  const [getAllSubjectTrackingData, { data, error, isError, isLoading }] = useSubjectTrackingDataMutation();
  const [allSubjectsIDsList, setAllSubjectsIDsList] = useState<string[]>([]);
  const [selectedSubjectID, setSelectedSubjectID] = useState(
    useAppSelector(state => state.selectedSubjectInfo.subject_id)
  );
  const [selectedVisitType, setSelectedVisitType] = useState(
    useAppSelector(state => state.selectedSubjectInfo.visit_type)
  );

  /** Refetching the study tracking data on hierarchy change */

  const getAllSubjectTrackingFunc = (from: number, size: number, data: any) => {
    getAllSubjectTrackingData({
      study_id: selectedStudy,
      site_id: site,
      country: selectedCountry,
      from,
      size,
      data
    });
  };

  useEffect(() => {
    if (pageSize === 10 || pageSize === 20 || pageSize === 30) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getAllSubjectTrackingFunc(0, pageSize, filterObject);
      } else {
        getAllSubjectTrackingFunc(pageNumber * pageSize, pageSize, filterObject);
      }
    }
  }, [pageNumber, pageSize, currentHierarchyLevel, selectedStudy, site, selectedCountry, filterObject]);

  const formatDate = (params: any) => {
    return preferedDateFormat(params.value);
  };

  const handleSubjectIDChangeCallBack = (subjectID: any) => {
    setSelectedSubjectID(subjectID);
    dispatch(
      setPreScreenGridRowsData(
        data?.selected_data?.row_data?.find((item: any) => item.subject_id && item.subject_id == subjectID)
      )
    );
  };
  const handleSubjectSelect: GridEventListener<'cellClick'> = (params: any) => {
    dispatch(clearSubjectInfo());
    setPrescreenId(params.row.pre_screen_id);
    dispatch(setPreScreenGridRowsData(params.row));
    setSelectedSubjectID(params.row.subject_id);
    setSelectedVisitType('All');
    setShowSubjectDetails(true);
    setShowSubjectTraking(false);
    dispatch(setSelectedSubject(params.row.subject_id));
  };
  const getSubjectTrackingDetails = async () => {
    try {
      dispatch(showLoader());
      // Change the mockData value to result
      const tempColumns: GridColDef[] = [];
      const tempGroups: GridColumnGroupingModel = [];
      if (mockData.column_header.length > 0) {
        mockData.column_header.map((group: any, index: number) => {
          const fieldsArray: any = [];
          group.fields.map((field: any) => {
            fieldsArray.push({ field: field.id });
            let tempCol: any = {};
            if (field.id === 'subject_id') {
              tempCol = {
                field: field.id,
                headerName: field.name,
                minWidth: field.width,
                type: field.type,
                flex: 1,
                renderCell: (params: any) => {
                  return params.row.subject_id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75 }}>
                      <PersonIcon sx={{ color: 'rgba(0,0,0,0.5)' }} /> <span>{params.row.subject_id}</span>
                    </Box>
                  ) : null;
                }
              };
            } else {
              tempCol = {
                field: field.id,
                headerName: field.name,
                minWidth: field.width,
                type: field.type,
                flex: 1
              };
            }
            field.type === 'date'
              ? tempColumns.push({ ...tempCol, valueGetter: formatDate })
              : tempColumns.push(tempCol);
          });
          const tempGroup: GridColumnGroupingModel = [
            {
              groupId: group.groupId,
              headerName: group.headerName,
              children: fieldsArray,
              headerAlign: 'center'
            }
          ];
          tempGroups.push(
            index % 2 === 0
              ? {
                  ...tempGroup[0]
                }
              : {
                  headerClassName: 'grouped-column--color',
                  ...tempGroup[0]
                }
          );
        });
      }
      setPreScreenGridColumns(tempColumns);
      setPreScreencolumnGroupingModel(tempGroups);
      dispatch(hideLoader());
    } catch (error: any) {
      dispatch(hideLoader());
      if (error.response.status === 403) {
        dispatch(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
      } else if (error.response.status === 401) {
        dispatch(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
      }
    }
  };

  useEffect(() => {
    if (isError) {
      setRows([]);
      setRowsCount(0);
      const dataErorr: any = error;
      if (dataErorr?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${dataErorr?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (dataErorr?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
  }, [isError]);

  useEffect(() => {
    if (data) {
      setRows(data.selected_data.row_data);
      setRowsCount(data.record_count);
      setAllSubjectsIDsList(
        data.selected_data.row_data.map((item: any) => item.subject_id).filter((subject: any) => subject != null)
      );
    }
  }, [data]);

  useEffect(() => {
    if (showSubjectTraking) {
      getSubjectTrackingDetails();
    }
  }, [showSubjectTraking]);

  const fromNavigationPathName: any = useAppSelector(state => {
    return state.navigationPath.navigationPathName;
  });
  useEffect(() => {
    if (fromNavigationPathName !== '' && fromNavigationPathName !== null) {
      const pathFromTo = fromNavigationPathName.split(':');
      if (pathFromTo[0] === 'dataReview') {
        getSubjectTrackingDetails();
        setShowSubjectDetails(true);
        setShowSubjectTraking(false);
      }
    }
  }, [fromNavigationPathName]);

  const dataTableProps = {
    apiRef,
    sx: {
      '& .MuiDataGrid-columnHeaderTitle': {
        // textOverflow: 'clip',
        whiteSpace: 'break-spaces',
        lineHeight: 1.3
      },
      '& .MuiDataGrid-cell:not(:empty)': { cursor: 'pointer' },
      '& .MuiDataGrid-cell': {
        '&:is([data-field="pre_screen_id"])': {
          fontWeight: 'bold',
          color: `#243F81`
        }
      }
    },
    onCellClick: handleSubjectSelect,
    experimentalFeatures: { columnGrouping: true },
    columnGroupingModel: preScreencolumnGroupingModel,
    getRowId: (row: any) => row.sl_no,
    filterMode: 'server'
  };

  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `Prescreen_Details ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };

  const filterValues = (data: any) => {
    setFilterObject(data);
  };

  return (
    <div className="subject-tracking-page">
      {showSubjectTraking && (
        <Grid container>
          <Grid item md={12}>
            <Typography variant="h6" component="h6" sx={{ mt: -1, mb: 1.5 }}>
              Prescreen Details
            </Typography>
            <Box
              sx={{
                height: '84vh',
                width: '100%',
                '& .grouped-column--color': {
                  backgroundColor: 'rgba(80, 115, 185, 0.08)',
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: 'rgba(80, 115, 185, 0.08)'
                  }
                },
                '@media (max-width:1367px)': {
                  height: '76.5vh'
                }
              }}
            >
              {preScreenGridColumns.length > 0 && preScreencolumnGroupingModel.length > 0 && (
                <DataTable
                  dataTableProps={dataTableProps}
                  height="76vh"
                  className="prescreenDatagrid"
                  rows={rows}
                  hasDefinedColumns
                  paging
                  columns={preScreenGridColumns}
                  pagination
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  setPageNumber={setPageNumber}
                  loading={isLoading}
                  datePreference={userPreferedDateFormat}
                  rowsCount={rowsCount}
                  pageNumber={pageNumber}
                  paginationMode="server"
                  dataTableInitialState={{
                    sorting: {
                      sortModel: [{ field: 'sl_no', sort: 'asc' }]
                    }
                  }}
                  hasDefinedDataGridToolbarExport
                  definedGridToolbarExport={definedCustomDatGridExport}
                  filterValues={(data: any) => filterValues(data)}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      )}
      {showSubjectDetails && (
        <Box sx={{ width: '100%' }}>
          <SubjectTrackingDetails
            prescreen_id={prescreenId}
            selectedSubjectID={selectedSubjectID}
            setSelectedSubjectID={setSelectedSubjectID}
            selectedVisitType={selectedVisitType}
            setSelectedVisitType={setSelectedVisitType}
            handleSubjectIDChangeCallBack={handleSubjectIDChangeCallBack}
            allSubjectsIDsList={allSubjectsIDsList}
            setShowSubjectTraking={setShowSubjectTraking}
            setShowSubjectDetails={setShowSubjectDetails}
          />
        </Box>
      )}
    </div>
  );
};

export default SubjectTrackingPage;
